import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useHistory } from "react-router-dom";
import { PageHeader, Button, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { yupResolver } from "@hookform/resolvers/yup";
import { sliceKey, reducer, actions } from "./slice";
import { logisticsAddSaga } from "./saga";
import * as selectors from "./selectors";
import AddLogisticsForm from "../../../../forms/AddLogisticsForm";
import routes from "../../../../../routes";
import schema from "../../../../schemas/addLogisticSchema";
import Loader from "../../../../components/Loader";

const AddLogistics = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: logisticsAddSaga });
  const dispatch = useDispatch();
  const history = useHistory();

  // effects
  useEffect(() => {
    dispatch(actions.fetchCountries());
    dispatch(actions.fetchStates());
    dispatch(actions.fetchCities());
    return () => {
      dispatch(actions.initializeState());
    };
  }, [dispatch]);

  // selectors
  const countries = useSelector(selectors.selectCountries);
  const states = useSelector(selectors.selectStates);
  const cities = useSelector(selectors.selectCities);
  const loading = useSelector(selectors.selectLoading);
  const error = useSelector(selectors.selectError);

  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error Creating New Logistics",
        description: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1500);
    }
  }, [error, dispatch]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      name: "",
      remarks: "",
      contract: "",
      pocName: "",
      pocContact: "",
      operatingStates: [],
      address: {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
      },
    },
    resolver: yupResolver(schema),
  });

  const selectedCountry = watch("address.country");
  const selectedState = watch("address.state");

  useEffect(() => {
    if (selectedCountry)
      dispatch(actions.fetchStates({ countryId: selectedCountry }));
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    if (selectedState)
      dispatch(actions.fetchCities({ stateId: selectedState }));
  }, [selectedState, dispatch]);

  const handleSuccess = () => {
    notification.success({
      message: "Logistic Partner got added",
    });
    history.push(routes.backOffice.logisticPartners.list);
  };

  const handleFailure = () => {
    console.log("An error occured");
  };

  const onSubmit = (data) => {
    data.name = data.name.trim().split(/ +/).join(" ");
    const payload = {
      onSuccess: handleSuccess,
      data,
      onFailure: handleFailure,
    };
    dispatch(actions.addLogistics(payload));
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="New Logistics Partner"
        extra={[
          <Button key="1">
            <Link to={routes.backOffice.logisticPartners.list}>Cancel</Link>
          </Button>,
          <Button
            key="2"
            type="primary"
            disabled={loading}
            loading={loading}
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Button>,
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        {loading ? <Loader /> : ""}
        <AddLogisticsForm
          options={{
            states,
            cities,
            countries,
          }}
          control={control}
          watch={watch}
          errors={errors}
          setValue={setValue}
        />
        <p
          style={{
            marginTop: "10px",
            color: "#75797d",
            fontSize: "12px",
            fontStyle: "italic",
          }}
        >
          * Uploaded file size should not exceed more than 12 MB
        </p>
      </div>
    </div>
  );
};

export default AddLogistics;
